import React, { useEffect, useState } from 'react';
import { Card, Button, Badge, Alert, Form, Row, Col } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { spotifyApi, spotifySettingsApi } from '../api/api';
import PlaylistPicker from '../components/PlaylistPicker';
import AllowedPlaylistsPicker from '../components/AllowedPlaylistsPicker';

export default function SpotifySettings() {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const connected = searchParams.get('connected');

  useEffect(() => {
    spotifyApi.status().then((r) => setStatus(r.data)).catch(() => {});
    spotifySettingsApi.get().then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const handleConnect = () => { setLoading(true); spotifyApi.connect(); };
  const handleDisconnect = async () => {
    await spotifyApi.disconnect();
    setStatus({ connected: false });
  };

  const update = (patch) => setSettings((prev) => ({ ...prev, ...patch }));

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await spotifySettingsApi.update(settings);
      setSettings(data);
      toast.success('Spotify-Einstellungen gespeichert!');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const fmtMs = (ms) => {
    if (!ms) return '0';
    const min = Math.floor(ms / 60000);
    const sec = Math.floor((ms % 60000) / 1000);
    return sec > 0 ? `${min}:${String(sec).padStart(2, '0')} min` : `${min} min`;
  };

  return (
    <div>
      <h4 className="text-white mb-4">🎵 Spotify-Einstellungen</h4>

      {connected && (
        <Alert variant="success" dismissible>Spotify erfolgreich verbunden!</Alert>
      )}

      <Row className="g-4">
        {/* OAuth Connection */}
        <Col md={5}>
          <Card bg="dark" text="white" className="border-secondary h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Card.Title>Spotify-Konto</Card.Title>
                <Badge bg={status?.connected ? 'success' : 'secondary'}>
                  {status?.connected ? 'Verbunden' : 'Nicht verbunden'}
                </Badge>
              </div>
              {status?.connected ? (
                <>
                  <p className="text-muted">
                    Eingeloggt als: <strong className="text-white">{status.display_name}</strong>
                  </p>
                  <p className="text-muted small">ID: {status.spotify_user_id}</p>
                  <Button variant="outline-danger" onClick={handleDisconnect}>
                    Spotify trennen
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-muted small mb-3">
                    Verbinde deinen eigenen Spotify-Account für aktuellen Song,
                    Song Requests und Wiedergabesteuerung.
                  </p>
                  <Button variant="success" onClick={handleConnect}
                    disabled={loading}>
                    {loading ? 'Weiterleitung…' : '🎵 Mit Spotify verbinden'}
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Per-user Spotify SR Settings */}
        {settings && (
          <Col md={7}>
            <Card bg="dark" text="white" className="border-secondary">
              <Card.Body>
                <Card.Title className="mb-3">Song-Request Regeln</Card.Title>
                <p className="text-muted small mb-3">
                  Diese Einstellungen gelten nur für deinen Channel — jeder Streamer konfiguriert sie selbst.
                </p>

                <Form.Check type="switch" label="Song Requests aktiviert"
                  checked={settings.sr_enabled}
                  onChange={(e) => update({ sr_enabled: e.target.checked })} className="mb-2" />
                <Form.Check type="switch" label="Explizite Songs erlauben"
                  checked={settings.allow_explicit}
                  onChange={(e) => update({ allow_explicit: e.target.checked })} className="mb-2" />
                <Form.Check type="switch" label="Doppelte Songs blockieren"
                  checked={settings.skip_duplicate_songs}
                  onChange={(e) => update({ skip_duplicate_songs: e.target.checked })} className="mb-3" />

                <Row className="mb-3">
                  <Col>
                    <Form.Label className="small">
                      Max. Songdauer: {settings.max_song_duration_ms > 0
                        ? fmtMs(settings.max_song_duration_ms)
                        : 'Kein Limit'}
                    </Form.Label>
                    <Form.Range min={0} max={900000} step={30000}
                      value={settings.max_song_duration_ms}
                      onChange={(e) => update({ max_song_duration_ms: +e.target.value })} />
                  </Col>
                  <Col>
                    <Form.Label className="small">
                      Min. Songdauer: {settings.min_song_duration_ms > 0
                        ? fmtMs(settings.min_song_duration_ms)
                        : 'Kein Limit'}
                    </Form.Label>
                    <Form.Range min={0} max={120000} step={5000}
                      value={settings.min_song_duration_ms}
                      onChange={(e) => update({ min_song_duration_ms: +e.target.value })} />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col>
                    <Form.Label className="small">
                      Cooldown pro Viewer: {settings.cooldown_per_user_seconds > 0
                        ? `${settings.cooldown_per_user_seconds}s`
                        : 'Kein Cooldown'}
                    </Form.Label>
                    <Form.Range min={0} max={3600} step={30}
                      value={settings.cooldown_per_user_seconds}
                      onChange={(e) => update({ cooldown_per_user_seconds: +e.target.value })} />
                  </Col>
                  <Col>
                    <Form.Label className="small">
                      Max. Requests pro Viewer: {settings.max_requests_per_user > 0
                        ? settings.max_requests_per_user
                        : 'Unbegrenzt'}
                    </Form.Label>
                    <Form.Range min={0} max={10}
                      value={settings.max_requests_per_user}
                      onChange={(e) => update({ max_requests_per_user: +e.target.value })} />
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <PlaylistPicker
                    value={settings.request_playlist_id}
                    onChange={(id) => update({ request_playlist_id: id || null })}
                    label="Request-Playlist"
                    allowCreate={true}
                  />
                  <Form.Text className="text-muted">
                    Songs aus Requests werden automatisch zu dieser Playlist hinzugefügt.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <PlaylistPicker
                    value={settings.save_to_playlist_id}
                    onChange={(id) => update({ save_to_playlist_id: id || null })}
                    label="Playlist-ID für !saveplaylist-Befehl"
                    allowCreate={true}
                  />
                  <Form.Text className="text-muted">
                    Moderatoren können mit !saveplaylist den aktuell spielenden Song hier speichern.
                  </Form.Text>
                </Form.Group>

                <AllowedPlaylistsPicker
                  value={settings.allowed_playlist_ids}
                  onChange={(ids) => update({ allowed_playlist_ids: ids })}
                />

                <Button variant="success" onClick={saveSettings} disabled={saving}>
                  {saving ? 'Speichere…' : '💾 Einstellungen speichern'}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}
